<template>
  <v-form ref="stepMphRef">
    <v-row>
      <v-col sm="12" lg="6" class="pa-6">
        <h3 class="pb-2">
          Informationen zum Anschlussort
        </h3>
        <v-row no-gutters>
          <v-col cols="auto">
            <v-checkbox
              v-model="formMphStore.buildingUnderConstruction"
              label="Gebäude im Bau"
              hide-details
            />
          </v-col>
          <v-col v-if="formMphStore.buildingUnderConstruction" offset-sm="1" sm="5" cols="12">
            <custom-date-picker
              label="Erstbezug ab Datum"
              is-required
              :min-date="dayjs()"
              @date-model="formMphStore.buildingUseFromDate = $event"
            />
          </v-col>
        </v-row>
        <v-row no-gutters class="mb-6">
          <v-col cols="6" class="pr-2">
            <v-text-field
              v-model="formMphStore.numberOfOwners"
              v-maska="numberMask"
              :rules="mandatory"
              hide-details="auto"
            >
              <template #label>
                <span>Anzahl Eigentümer<sup v-if="mandatory" class="required">*</sup></span>
              </template>
            </v-text-field>
          </v-col>
          <v-col cols="6" class="pr-2">
            <v-text-field
              v-model="formMphStore.numberOfFloors"
              v-maska="numberMask"
              label="Anzahl Etagen"
              hide-details="auto"
            />
          </v-col>
        </v-row>
        <h4 class="py-2">
          Besteht eine Wohnungseigentümergemeinschaft?
        </h4>
        <div class="d-flex align-center">
          <v-switch
            v-model="formMphStore.isCommunityOfOwners"
            :rules="mandatoryBoolean"
            label="Ja"
            hide-details
          />
        </div>
        <h4 v-if="formMphStore.isCommunityOfOwners" class="mt-4 mb-2">
          Existiert ein Verwalter?
        </h4>
        <div v-if="formMphStore.isCommunityOfOwners" class="d-flex align-center">
          <v-switch
            v-model="formMphStore.existsAnAdministrator"
            :rules="mandatoryBoolean"
            label="Ja"
            hide-details
          />
        </div>
        <template v-if="formMphStore.isCommunityOfOwners && formMphStore.existsAnAdministrator">
          <v-divider class="my-4" />
          <div>
            <household-basic-information
              class="pt-0"
              required
              stakeholder="Verwalter"
              with-company-option
              :model-value="formMphStore.administrator?.household"
              @update:model-value="updateAdministratorHousehould($event)"
            />
            <address-form
              :model-value="formMphStore.administrator?.address"
              @update:model-value="updateAdministratorAddress($event)"
            />
            <household-contact-data
              :model-value="formMphStore.administrator?.contact"
              :phone-prefixes="initStore.phonePrefixes!"
              @update:model-value="updateAdministratorContact($event)"
            />
          </div>
        </template>
      </v-col>
      <v-col sm="12" lg="6" class="px-4 pt-0">
        <v-container fluid>
          <h3 class="pt-4 pb-2">
            Preis der Innenverkabelung für den gesamten Gebäudekomplex
          </h3>
          <v-row no-gutters>
            <v-col cols="12" sm="3">
              <v-text-field
                v-model="formMphStore.unitsResidential"
                v-maska="numberMask"
                class="py-2"
                hide-details="auto"
                :rules="rulesMinimumUnits"
              >
                <template #label>
                  <span># Wohneinheiten<sup v-if="rulesMinimumUnits" class="required">*</sup></span>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" sm="3" class="ml-sm-2">
              <v-text-field
                v-model="formMphStore.unitsCommercial"
                v-maska="numberMask"
                class="py-2"
                hide-details="auto"
                :rules="rulesMinimumUnits"
              >
                <template #label>
                  <span>
                    # Gewerbeeinheiten<sup v-if="rulesMinimumUnits" class="required">*</sup>
                  </span>
                </template>
              </v-text-field>
            </v-col>
            <v-col
              v-if="formOrderRequestStore.ne4OrderType === 'complete'"
              class="ml-sm-2"
              sm="3"
              cols="12"
            >
              <v-text-field
                v-model="formMphStore.ne4Discount"
                v-maska="numberMask"
                class="py-2"
                label="Rabatt"
                hide-details="auto"
                :rules="rulesDiscount"
                append-inner-icon="mdi-currency-eur"
              />
            </v-col>
            <v-col cols="12" sm="auto" class="ml-sm-2 px-2">
              <h4 class="mt-2">
                Gesamtpreis
              </h4>
              <h3>{{ formatPriceWithCurrency(formMphStore.discountedPrice) }}</h3>
            </v-col>
            <v-col cols="12">
              <v-alert class="body-2 pa-4 mt-2">
                <span class="font-weight-medium">Info: Die Zahlung erfolgt per Überweisung</span>
                <v-divider class="my-1 info" style="opacity: 0.22" />
                Je Wohnung / Geschäft bzw. Büro fällt eine Pauschale von
                <b>{{ formatPriceWithCurrency(formMphStore.singleExtensionPrice) }}</b> an.
                Beauftragung nur für den Vollausbau möglich.
              </v-alert>
            </v-col>
          </v-row>
          <v-row no-gutters class="mt-4 mb-6">
            <v-col cols="12">
              <h4 class="pt-4 pb-2">
                Ansprechpartner vor Ort
              </h4>
              <v-radio-group
                v-model="formMphStore.localContactPerson"
                :rules="buildRulesRequiredDefined<LocalContactPerson>()"
              >
                <v-radio
                  label="Auftraggeber und Ansprechpartner vor Ort sind identisch"
                  :value="LocalContactPerson.ORDERER"
                />
                <v-radio
                  v-if="formMphStore.isCommunityOfOwners && formMphStore.existsAnAdministrator"
                  label="Verwalter und Ansprechpartner vor Ort sind identisch"
                  :value="LocalContactPerson.ADMINISTRATOR"
                />
                <v-radio
                  label="Abweichender Ansprechpartner angeben"
                  :value="LocalContactPerson.OTHER"
                />
              </v-radio-group>
            </v-col>
          </v-row>
        </v-container>
        <template v-if="formMphStore.localContactPerson === LocalContactPerson.OTHER">
          <v-divider />
          <v-container fluid>
            <v-col cols="12">
              <household-basic-information
                v-model="formMphStore.contactPerson!.household"
                class="pt-0"
                with-company-option
                stakeholder="Ansprechpartner"
              />
              <household-contact-data
                v-model="formMphStore.contactPerson!.contact"
                :phone-prefixes="initStore.phonePrefixes!"
              />
            </v-col>
          </v-container>
        </template>
      </v-col>
    </v-row>
  </v-form>
</template>
<script setup lang="ts">
import dayjs from 'dayjs';
import type { MaskInputOptions } from 'maska';
import { ref, computed, reactive } from 'vue';
import { buildRulesRequiredDefined, mandatory, mandatoryBoolean } from '../composables/validations';
import { formatPriceWithCurrency } from '../composables/formatter';
import AddressForm from './common/address-form.vue';
import CustomDatePicker from './common/custom-date-picker.vue';
import HouseholdBasicInformation from './common/household-basic-information.vue';
import HouseholdContactData from './common/household-contact-data.vue';
import useFormMphStore from '../store/form-mph';
import useFormOrderRequestStore from '../store/form-order-request';
import useInitStore from '../store/init';
import useOrderStore from '../store/order';
import type { Household } from '../types/household';
import type { Address } from '../types/address';
import type { Contact } from '../types/common';
import { LocalContactPerson } from '../types/step-data';

const formMphStore = useFormMphStore();
const formOrderRequestStore = useFormOrderRequestStore();
const initStore = useInitStore();

const stepMphRef = ref();

const numberMask = reactive<MaskInputOptions>({
  mask: '###########',
  eager: true,
});

const rulesMinimumUnits = computed(() => [
  () => formMphStore.totalUnits > 1 || 'Beauftragung nur für mehrere Einheiten möglich',
]);

const isValid = async () => {
  const { valid } = await stepMphRef.value.validate();
  return valid;
};

const rulesDiscount = computed(() => [
  () => formMphStore.finalDiscount <= formMphStore.totalPrice || 'Rabatt übersteigt den Preis.',
]);

function updateAdministratorHousehould(household: Household) {
  formMphStore.administrator = { ...formMphStore.administrator!, household };
}
function updateAdministratorAddress(address: Address) {
  formMphStore.administrator = { ...formMphStore.administrator!, address };
}
function updateAdministratorContact(contact: Contact) {
  formMphStore.administrator = { ...formMphStore.administrator!, contact };
}

defineExpose({
  applyData: () => {
    useOrderStore().addMphData(formMphStore.stepData);
  },
  isValid,
});
</script>
